import React, { useEffect, useMemo, useState } from 'react';
import Checkbox from '@mui/material/Checkbox';
import { Field, useFormikContext } from 'formik';

import { CheckboxUI, FormErrorUI, FormLabelUI, InputUI } from '../Interface';
import { ITEM_TYPE_PROPERTY_DATA_TYPES } from '../ItemTypeProperty/ItemTypePropertyFields';

const ItemTypeFields = ({ viewField, autocompleteOptions }) => {
  const { values, errors, setFieldValue, validateField } = useFormikContext();
  const [propertySearch, setPropertySearch] = useState('');

  useEffect(() => {
    if (viewField) {
      setPropertySearch('');
    }
  }, [viewField, values.id]);

  const dataTypeMap = useMemo(() => {
    return ITEM_TYPE_PROPERTY_DATA_TYPES.reduce((acc, item) => {
      acc[item.value] = item.label;
      return acc;
    }, {});
  }, []);

  const filteredItemTypeProperties = useMemo(() => {
    const normalizedSearch = propertySearch.trim().toLowerCase();

    if (!normalizedSearch) return autocompleteOptions.itemTypeProperty;

    return autocompleteOptions.itemTypeProperty.filter((item) => {
      const searchableText = [item.name, item.externalCode, dataTypeMap[item.dataType]]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchableText.includes(normalizedSearch);
    });
  }, [autocompleteOptions.itemTypeProperty, dataTypeMap, propertySearch]);

  const handlePropertyToggle = (propertyId, checked) => {
    const currentIds = values.itemTypePropertyIds ?? [];
    const nextIds = checked
      ? [propertyId, ...currentIds.filter((id) => id !== propertyId)]
      : currentIds.filter((id) => id !== propertyId);

    setFieldValue('itemTypePropertyIds', nextIds).then(() => {
      validateField('itemTypePropertyIds');
    });
  };

  const renderPropertyLabel = (item) => {
    const typeLabel = dataTypeMap[item.dataType] ?? item.dataType ?? '';
    const externalCodeText = item.externalCode ? ` [${item.externalCode}]` : '';
    return `${item.name}${externalCodeText}${typeLabel ? ` (${typeLabel})` : ''}`;
  };

  return (
    <div className="grid grid-cols-4 gap-x-4 gap-y-2 mx-4 mb-5">
      <div className="col-span-4 md:col-span-2">
        <Field
          type="text"
          name="name"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Név" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.name} />}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.name) }}
        />
      </div>
      <div className="col-span-4 md:col-span-2">
        <Field
          type="text"
          name="externalCode"
          as={InputUI}
          fullWidth
          label={<FormLabelUI text="Külső azonosító" />}
          variant="standard"
          helperText={<FormErrorUI message={errors.externalCode} />}
          disabled={viewField}
          InputLabelProps={{ shrink: Boolean(values.externalCode) }}
        />
      </div>
      <div className="col-span-4">
        <div>
          <div className="mb-2">
            <FormLabelUI text="Kapcsolódó tulajdonságok" />
          </div>
          <div className="border border-[#c8d2db] rounded-sm bg-white">
            <div className="p-2 border-b border-[#d8e0e7]">
              <InputUI
                value={propertySearch}
                onChange={(e) => setPropertySearch(e.target.value)}
                fullWidth
                variant="standard"
                disabled={viewField}
                label={<FormLabelUI text="Keresés" />}
                InputLabelProps={{ shrink: Boolean(propertySearch) }}
              />
            </div>
            <div className="h-[260px] overflow-auto px-2 py-1">
              {filteredItemTypeProperties.map((item) => (
                <label
                  key={item.value}
                  className={`flex items-start gap-2 py-2 px-1 border-b border-[#eef2f5] last:border-b-0 ${
                    viewField ? 'cursor-default' : 'cursor-pointer'
                  }`}
                >
                  <Checkbox
                    size="small"
                    disabled={viewField}
                    checked={values.itemTypePropertyIds?.includes(item.value) ?? false}
                    onChange={(e) => handlePropertyToggle(item.value, e.target.checked)}
                    sx={{ mt: '-2px' }}
                  />
                  <span className="text-sm leading-6">{renderPropertyLabel(item)}</span>
                </label>
              ))}
              {filteredItemTypeProperties.length === 0 && (
                <div className="px-1 py-3 text-sm text-slate-500">Nincs találat.</div>
              )}
            </div>
            <div className="px-2 pb-2 text-xs text-slate-500">
              <FormErrorUI message={errors.itemTypePropertyIds} />
            </div>
          </div>
        </div>
      </div>
      <div className="col-span-4 md:col-span-2">
        <Field
          name="active"
          type="checkbox"
          as={CheckboxUI}
          fullWidth
          label={<FormLabelUI text="Aktív" />}
          disabled={viewField}
          checked={values.active === true}
        />
      </div>
    </div>
  );
};

export default ItemTypeFields;
